<template>
    <span class="form-colorpicker border">
        <color-picker
            v-model:pureColor="pureColor"
            v-model:gradientColor="gradientColor"
            format="hex8"
            :theme="theme"
            useType="pure"
            :disableAlpha="disableAlpha"
            :disableHistory="true"
        />
    </span>
</template>
<script lang="ts" setup>
/**
 * This is a wrapper for the vue3 colorpicker component.
 * We use this one to have a consistent look and feel for all color pickers.
 * This one is theme aware and will change its appearance based on the current theme.
 *
 * @see https://github.com/aesoper101/vue3-colorpicker
 * @see https://aesoper101.github.io/vue3-colorpicker/?path=/docs/example-introduction--docs
 */
import { useThemeStoreRef } from '@/stores';
import { computed, ref, watch } from 'vue';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';

const props = withDefaults(
    defineProps<{
        /**
         * If enabled, then the alpha channel will be disabled.
         * @default true
         */
        disableAlpha?: boolean;
    }>(),
    {
        disableAlpha: true,
    },
);

const pureColorModel = defineModel<string | null>('pureColor');
const gradientColorModel = defineModel<string>('gradientColor');

// Wrap the pureColor in a computed so it doesnt immediatly overwrite the input model if it is null
// Default to transparent
const pureColor = computed({
    get: () => (pureColorModel.value ? pureColorModel.value : '#00000000'),
    set: (val: string) => {
        pureColorModel.value = val;
    },
});

// Wrap in computed so we dont write to the model directly.
// There is something strange here tho. If the value is '' we use a default value.
// Otherwise, we use the value from the model, but that can be undefined!
// So its abit unclear what is supposed to happen here. If we set the default when we have undefined, we get a warning in the console that the value cant be parsed.
// My best guess is that the gradient thing doesnt really work, and its always undefined in all the call sites
const gradientColor = computed({
    get: () => (gradientColorModel.value == '' ? '#00000000' : gradientColorModel.value),
    set: (val: string) => {
        gradientColorModel.value = val;
    },
});

const { currentTheme } = useThemeStoreRef();
const theme = ref<'black' | 'white'>('black');

watch(
    () => currentTheme.value!,
    (nv) => (nv == 'dark' ? (theme.value = 'black') : (theme.value = 'white')),
    { immediate: true },
);
</script>
